import { API_BASE_URL } from "../../config/apiConfig";



import { useCallback, useEffect, useRef, useState } from "react";
import {
  BadgeCheck,
  CreditCard,
  MoreVertical,
  ShieldCheck,
  Star,
} from "lucide-react";
import { professions } from "../../data/professions";
import { getRegulator } from "../../data/regulators";
import { licenceTypes } from "../../data/licenceTypes";
import { registrationClasses } from "../../data/registrationClasses";

import PageLoader from "../../components/common/PageLoader";
import "../../styles/components/ProfessionalLicenceModal.css";
import "../../styles/pages/professional/ProfessionalLicenses.css";

const API_URL = `${API_BASE_URL}/professionals/licenses`;

const DOCUMENT_API_URL =
  `${API_BASE_URL}/documents`;

const provinces = [
  "Alberta",
  "British Columbia",
  "Manitoba",
  "New Brunswick",
  "Newfoundland and Labrador",
  "Northwest Territories",
  "Nova Scotia",
  "Nunavut",
  "Ontario",
  "Prince Edward Island",
  "Quebec",
  "Saskatchewan",
  "Yukon",
];

const emptyFormData = {
  profession: "",
  province: "",
  regulatoryBody: "",
  regulatorAbbreviation: "",
  regulatorWebsite: "",
  verificationUrl: "",
  licenceNumber: "",
  licenceType: "",
  registrationClass: "",
  issueDate: "",
  expiryDate: "",
  isPrimary: false,
};

const toDateInputValue = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
};

const ProfessionalLicenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLicence, setEditingLicence] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [
  showLoadingIndicator,
  setShowLoadingIndicator,
] = useState(false);
  const [selectedDocument, setSelectedDocument] =
  useState(null);

  useEffect(() => {
  if (!loading) {
    setShowLoadingIndicator(false);
    return;
  }

  const timer = window.setTimeout(() => {
    setShowLoadingIndicator(true);
  }, 300);

  return () => {
    window.clearTimeout(timer);
  };
}, [loading]);

const [currentDocument, setCurrentDocument] =
  useState(null);

const [documentLoading, setDocumentLoading] =
  useState(false);

const [documentActionLoading, setDocumentActionLoading] =
  useState(false);

  const menuRef = useRef(null);

  const fetchLicenses = useCallback(async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("zethAuthToken");

      if (!token) {
        throw new Error("You are not logged in. Please log in again.");
      }

      const response = await fetch(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Unable to retrieve licences.");
      }

      setLicenses(data.licences || []);
    } catch (error) {
      console.error("Licence request failed:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpenMenuId(null);

        if (showModal && !saving) {
          setShowModal(false);
          setEditingLicence(null);
          setFormError("");
          setFormData({ ...emptyFormData });
        }
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [saving, showModal]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((previousData) => {
      const updatedData = {
        ...previousData,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "profession") {
        updatedData.registrationClass = "";
      }

      if (name === "province" || name === "profession") {
        const regulatorInfo = getRegulator(
          updatedData.province,
          updatedData.profession
        );

        if (typeof regulatorInfo === "string") {
          updatedData.regulatoryBody = regulatorInfo;
          updatedData.regulatorAbbreviation = "";
          updatedData.regulatorWebsite = "";
          updatedData.verificationUrl = "";
        } else {
          updatedData.regulatoryBody = regulatorInfo?.regulator || "";
          updatedData.regulatorAbbreviation =
            regulatorInfo?.abbreviation || "";
          updatedData.regulatorWebsite = regulatorInfo?.website || "";
          updatedData.verificationUrl =
            regulatorInfo?.verificationUrl || "";
        }
      }

      return updatedData;
    });
  };

const resetDocumentState = () => {
  setSelectedDocument(null);
  setCurrentDocument(null);
  setDocumentLoading(false);
  setDocumentActionLoading(false);
};

const fetchLicenceDocument = async (licenceId) => {
  try {
    setDocumentLoading(true);
    setCurrentDocument(null);

    const token = localStorage.getItem(
      "zethAuthToken"
    );

    if (!token) {
      throw new Error(
        "You are not logged in. Please log in again."
      );
    }

    const response = await fetch(
      `${DOCUMENT_API_URL}/professional_license/${licenceId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.status === 404) {
      setCurrentDocument(null);
      return;
    }

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(
        data.message ||
          "Unable to retrieve the licence document."
      );
    }

    setCurrentDocument(data.document || null);
  } catch (error) {
    console.error(
      "Licence document request failed:",
      error
    );

    setFormError(error.message);
  } finally {
    setDocumentLoading(false);
  }
};

const validateSelectedDocument = (file) => {
  if (!file) {
    return;
  }

  const allowedTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
  ];

  const maximumSize = 10 * 1024 * 1024;

  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      "Only PDF, JPG, JPEG and PNG files are allowed."
    );
  }

  if (file.size > maximumSize) {
    throw new Error(
      "The licence document must not exceed 10 MB."
    );
  }
};

const handleDocumentChange = (event) => {
  try {
    setFormError("");

    const file = event.target.files?.[0] || null;

    validateSelectedDocument(file);
    setSelectedDocument(file);
  } catch (error) {
    event.target.value = "";
    setSelectedDocument(null);
    setFormError(error.message);
  }
};

const uploadLicenceDocument = async (
  licenceId,
  token
) => {
  if (!selectedDocument) {
    return null;
  }

  validateSelectedDocument(selectedDocument);

  const documentFormData = new FormData();

  documentFormData.append(
    "relatedRecordId",
    String(licenceId)
  );

  documentFormData.append(
    "document",
    selectedDocument
  );

  documentFormData.append(
    "notes",
    "Professional licence copy"
  );

  const response = await fetch(
    `${DOCUMENT_API_URL}/upload/professional_license`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: documentFormData,
    }
  );

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(
      data.message ||
        "The licence was saved, but its document could not be uploaded."
    );
  }

  setCurrentDocument(data.document || null);
  setSelectedDocument(null);

  return data.document;
};

const handleDownloadDocument = async () => {
  if (!currentDocument?.id) {
    return;
  }

  try {
    setDocumentActionLoading(true);

    const token = localStorage.getItem(
      "zethAuthToken"
    );

    if (!token) {
      throw new Error(
        "You are not logged in. Please log in again."
      );
    }

    const response = await fetch(
      `${DOCUMENT_API_URL}/download/${currentDocument.id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      let message =
        "Unable to download the licence document.";

      try {
        const data = await response.json();
        message = data.message || message;
      } catch {
        // The response might not contain JSON.
      }

      throw new Error(message);
    }

    const fileBlob = await response.blob();
    const downloadUrl =
      window.URL.createObjectURL(fileBlob);

    const link = document.createElement("a");

    link.href = downloadUrl;
    link.download =
      currentDocument.file_name ||
      "licence-document";

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    setFormError(error.message);
  } finally {
    setDocumentActionLoading(false);
  }
};

const handleDeleteDocument = async () => {
  if (!currentDocument?.id) {
    return;
  }

  const confirmed = window.confirm(
    `Delete "${currentDocument.file_name}"?`
  );

  if (!confirmed) {
    return;
  }

  try {
    setDocumentActionLoading(true);
    setFormError("");

    const token = localStorage.getItem(
      "zethAuthToken"
    );

    if (!token) {
      throw new Error(
        "You are not logged in. Please log in again."
      );
    }

    const response = await fetch(
      `${DOCUMENT_API_URL}/${currentDocument.id}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(
        data.message ||
          "Unable to delete the licence document."
      );
    }

    setCurrentDocument(null);
    setSelectedDocument(null);
  } catch (error) {
    setFormError(error.message);
  } finally {
    setDocumentActionLoading(false);
  }
};

  const handleOpenAddModal = () => {
  setOpenMenuId(null);
  setEditingLicence(null);
  setFormError("");
  setFormData({ ...emptyFormData });
  resetDocumentState();
  setShowModal(true);
};

const handleEditLicence = async (license) => {
  setOpenMenuId(null);
  setEditingLicence(license);
  setFormError("");
  setSelectedDocument(null);
  setCurrentDocument(null);

  setFormData({
    profession: license.profession || "",
    province: license.province || "",
    regulatoryBody:
      license.regulatory_body || "",
    regulatorAbbreviation:
      license.regulator_abbreviation || "",
    regulatorWebsite:
      license.regulator_website || "",
    verificationUrl:
      license.verification_url || "",
    licenceNumber:
      license.licence_number || "",
    licenceType:
      license.licence_type || "",
    registrationClass:
      license.registration_class || "",
    issueDate: toDateInputValue(
      license.issue_date
    ),
    expiryDate: toDateInputValue(
      license.expiry_date
    ),
    isPrimary: Boolean(
      license.is_primary
    ),
  });

  setShowModal(true);

  await fetchLicenceDocument(license.id);
};

  const handleCloseModal = () => {
  if (saving || documentActionLoading) {
    return;
  }

  setShowModal(false);
  setEditingLicence(null);
  setFormError("");
  setFormData({ ...emptyFormData });
  resetDocumentState();
};

 const handleSubmitLicence = async (event) => {
  event.preventDefault();

  try {
    setSaving(true);
    setFormError("");

    const token = localStorage.getItem(
      "zethAuthToken"
    );

    if (!token) {
      throw new Error(
        "You are not logged in. Please log in again."
      );
    }

    if (!formData.regulatoryBody) {
      throw new Error(
        "No regulatory body was found. Select a supported profession and province."
      );
    }

    if (selectedDocument) {
      validateSelectedDocument(
        selectedDocument
      );
    }

    const isEditing =
      Boolean(editingLicence);

    const requestUrl = isEditing
      ? `${API_URL}/${editingLicence.id}`
      : API_URL;

    const response = await fetch(
      requestUrl,
      {
        method: isEditing
          ? "PUT"
          : "POST",

        headers: {
          "Content-Type":
            "application/json",

          Authorization:
            `Bearer ${token}`,
        },

        body: JSON.stringify(formData),
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(
        data.message ||
          (isEditing
            ? "Unable to update licence."
            : "Unable to add licence.")
      );
    }

    const savedLicence =
      data.licence ||
      data.license ||
      data.data ||
      null;

    const savedLicenceId =
      savedLicence?.id ||
      editingLicence?.id;

    if (
      selectedDocument &&
      !savedLicenceId
    ) {
      throw new Error(
        "The licence was saved, but its ID was not returned. The document could not be attached."
      );
    }

    if (
      selectedDocument &&
      savedLicenceId
    ) {
      await uploadLicenceDocument(
        savedLicenceId,
        token
      );
    }

    setShowModal(false);
    setEditingLicence(null);
    setFormData({
      ...emptyFormData,
    });
    resetDocumentState();

    await fetchLicenses();
  } catch (error) {
    console.error(
      "Save licence error:",
      error
    );

    setFormError(error.message);
  } finally {
    setSaving(false);
  }
};

  const handleDeleteLicence = async (licenceId) => {
    try {
      const token = localStorage.getItem("zethAuthToken");

      if (!token) {
        throw new Error("You are not logged in. Please log in again.");
      }

      const response = await fetch(`${API_URL}/${licenceId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Unable to remove licence.");
      }

      setOpenMenuId(null);
      await fetchLicenses();
    } catch (error) {
      window.alert(error.message);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const activeLicenses = licenses.filter(
    (license) => license.licence_status?.toLowerCase() === "active"
  ).length;

  const verifiedLicenses = licenses.filter(
  (license) =>
    license.verification_status?.toLowerCase() === "verified"
).length;

const primaryLicense = licenses.find(
  (license) => Boolean(license.is_primary)
);

const professionShortName = {
  "Registered Nurse": "RN",
  "Registered Practical Nurse": "RPN",
  "Nurse Practitioner": "NP",
  "Personal Support Worker": "PSW",
  "Respiratory Therapist": "RT",
};

const primaryLicenseSummary = primaryLicense
  ? professionShortName[primaryLicense.profession] ||
    primaryLicense.profession
  : "Not selected";

const primaryLicenseProvince = primaryLicense
  ? primaryLicense.province
  : "";

  if (loading && showLoadingIndicator) {
  return (
    <PageLoader
      title="Professional Licences"
      message="Loading your licences..."
    />
  );
}

if (loading) {
  return null;
}

  return (
    <main className="professional-licenses">
      
      <header className="licences-page-header">
        <div>
          
<p className="licences-page-description">
  Add one or more professional licences to your profile.
</p>

<p className="licence-helper-text">
  You may add more than one professional licence if you are
  registered in different provinces, professions, or regulatory
  colleges.
</p>
        </div>

        <button
          type="button"
          className="primary-button add-licence-button"
          onClick={handleOpenAddModal}
        >
          <span aria-hidden="true">+</span>
          Add Professional Licence
        </button>
      </header>

      <section
        className="licences-summary-grid"
        aria-label="Licence summary"
      >
        <article className="licence-summary-card">
  <div className="licence-summary-icon licence-summary-icon-blue">
    <CreditCard size={22} strokeWidth={2} />
  </div>

  <div className="licence-summary-content">
    <span className="licence-summary-label">
      Professional Licences
    </span>

    <strong className="licence-summary-value">
      {licenses.length}
    </strong>

    <small>Total licences added</small>
  </div>
</article>

<article className="licence-summary-card">
  <div className="licence-summary-icon licence-summary-icon-green">
    <Star size={22} strokeWidth={2} />
  </div>

  <div className="licence-summary-content">
    <span className="licence-summary-label">
      Primary Licence
    </span>

    <strong className="licence-primary-summary">
      {primaryLicenseSummary}
    </strong>

    {primaryLicenseProvince && (
      <span className="licence-primary-province">
        {primaryLicenseProvince}
      </span>
    )}

    <small>
      {primaryLicense
        ? "Main professional registration"
        : "Choose a primary licence"}
    </small>
  </div>
</article>

<article className="licence-summary-card">
  <div className="licence-summary-icon licence-summary-icon-blue">
    <ShieldCheck size={22} strokeWidth={2} />
  </div>

  <div className="licence-summary-content">
    <span className="licence-summary-label">
      Active Licences
    </span>

    <strong className="licence-summary-value">
      {activeLicenses}
    </strong>

    <small>Currently active</small>
  </div>
</article>

<article className="licence-summary-card">
  <div className="licence-summary-icon licence-summary-icon-purple">
    <BadgeCheck size={22} strokeWidth={2} />
  </div>

  <div className="licence-summary-content">
    <span className="licence-summary-label">
      Verified Licences
    </span>

    <strong className="licence-summary-value">
      {verifiedLicenses}
    </strong>

    <small>Verified by Zeth Health</small>
  </div>
</article>
      </section>

      <section className="licences-content-card">
        <div className="licences-content-header">
          <div>
            <h2>Your Licences</h2>
            <p>
              Keep your licence information current to remain eligible for
              assignments.
            </p>
          </div>

          <span className="licence-count">
  {licenses.length} Professional{" "}
  {licenses.length === 1 ? "Licence" : "Licences"}
</span>
        </div>

        {licenses.length === 0 ? (
          <div className="licences-empty-state">
            <div className="licences-empty-icon" aria-hidden="true">
              📋
            </div>

            <h3>No professional licences added yet</h3>

<p>
  Add your first professional licence. You can add more than one
  licence if you are registered in multiple provinces or professions.
</p>
            <button
              type="button"
              className="primary-button"
              onClick={handleOpenAddModal}
            >
              + Add Your Professional First Licence
            </button>
          </div>
        ) : (
          <div className="licences-table-wrapper">
            <table className="licences-table">
              <thead>
                <tr>
                  <th>Province</th>
                  <th>Profession</th>
                  <th>Licence Number</th>
                  <th>Status</th>
                  <th>Verification</th>
                  <th>Expiry</th>
                  <th className="licence-menu-heading">
                    <span className="sr-only">Licence options</span>
                  </th>
                </tr>
              </thead>

              <tbody>
                {licenses.map((license) => {
                  const licenceStatus =
                    license.licence_status || "Not provided";
                  const verificationStatus =
                    license.verification_status || "Pending";

                  const licenceStatusClass =
                    licenceStatus.toLowerCase() === "active"
                      ? "status-active"
                      : licenceStatus.toLowerCase() === "expired"
                        ? "status-expired"
                        : "status-neutral";

                  const verificationClass =
                    verificationStatus.toLowerCase() === "verified"
                      ? "verification-verified"
                      : verificationStatus.toLowerCase() === "rejected"
                        ? "verification-rejected"
                        : "verification-pending";

                  return (
                    <tr key={license.id} className="licence-table-row">
                      <td>
                        <div className="province-cell">
                          <span
                            className="province-marker"
                            aria-hidden="true"
                          >
                            {license.province?.charAt(0) || "—"}
                          </span>
                          <strong>{license.province}</strong>
                        </div>
                      </td>

                      <td>{license.profession}</td>

                      <td>
                        <span className="licence-number">
                          {license.licence_number}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`licence-status-badge ${licenceStatusClass}`}
                        >
                          {licenceStatus}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`licence-status-badge ${verificationClass}`}
                        >
                          {verificationStatus}
                        </span>
                      </td>

                      <td>
                        <span className="licence-expiry-date">
                          {formatDate(license.expiry_date)}
                        </span>
                      </td>

                      <td className="licence-menu-cell">
                        <div
                          className="licence-menu-wrapper"
                          ref={openMenuId === license.id ? menuRef : null}
                        >
                          <button
                            type="button"
                            className="licence-menu-button"
                            aria-label={`Open menu for ${license.province} licence`}
                            aria-haspopup="menu"
                            aria-expanded={openMenuId === license.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              setOpenMenuId((currentId) =>
                                currentId === license.id ? null : license.id
                              );
                            }}
                          >
                            <MoreVertical size={18} strokeWidth={2} />
                          </button>

                          {openMenuId === license.id && (
                            <div
                              className="licence-row-menu"
                              role="menu"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => handleEditLicence(license)}
                              >
                                Edit Licence
                              </button>

                              <button
                                type="button"
                                role="menuitem"
                                className="remove-menu-option"
                                onClick={() => {
                                  setOpenMenuId(null);

                                  const confirmed = window.confirm(
                                    `Remove the ${license.profession} licence for ${license.province}?`
                                  );

                                  if (confirmed) {
                                    handleDeleteLicence(license.id);
                                  }
                                }}
                              >
                                Remove Licence
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showModal && (
        <div className="modal-overlay" onMouseDown={handleCloseModal}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="licence-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="licence-modal-title">
                {editingLicence
                  ? "Edit Professional Licence"
                  : "Add Professional Licence"}
              </h2>

              <button
                type="button"
                className="modal-close"
                onClick={handleCloseModal}
                aria-label="Close"
                disabled={saving}
              >
                
              </button>
            </div>

            {formError && (
              <div className="form-error" role="alert">
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmitLicence}>
              <div className="licence-form-grid">
                <div className="form-group">
                  <label htmlFor="profession">Profession *</label>
                  <select
                    id="profession"
                    name="profession"
                    value={formData.profession}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select profession</option>
                    {professions.map((profession) => (
                      <option key={profession} value={profession}>
                        {profession}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="province">Province or Territory *</label>
                  <select
                    id="province"
                    name="province"
                    value={formData.province}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select province or territory</option>
                    {provinces.map((province) => (
                      <option key={province} value={province}>
                        {province}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="regulatoryBody">Regulatory Body *</label>
                  <input
                    id="regulatoryBody"
                    name="regulatoryBody"
                    type="text"
                    value={formData.regulatoryBody}
                    placeholder="Select a profession and province"
                    title={formData.regulatoryBody}
                    readOnly
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="regulatorAbbreviation">
                    Regulator Abbreviation
                  </label>
                  <input
                    id="regulatorAbbreviation"
                    name="regulatorAbbreviation"
                    type="text"
                    value={formData.regulatorAbbreviation}
                    placeholder="Automatically populated"
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label>Regulator Website</label>
                  {formData.regulatorWebsite ? (
                    <a
                      href={formData.regulatorWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="regulator-link"
                      title={`Open ${formData.regulatorWebsite}`}
                    >
                      Visit regulator website
                    </a>
                  ) : (
                    <input
                      type="text"
                      value=""
                      placeholder="Automatically populated"
                      readOnly
                    />
                  )}
                </div>

                <div className="form-group full-width">
                  <label>Public Licence Verification</label>
                  {formData.verificationUrl ? (
                    <a
                      href={formData.verificationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="verify-licence-button"
                    >
                      Verify Licence
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="verify-licence-button disabled"
                      disabled
                    >
                      Verification link unavailable
                    </button>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="licenceNumber">Licence Number *</label>
                  <input
                    id="licenceNumber"
                    name="licenceNumber"
                    type="text"
                    value={formData.licenceNumber}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="licenceType">Licence Type</label>
                  <select
                    id="licenceType"
                    name="licenceType"
                    value={formData.licenceType}
                    onChange={handleInputChange}
                  >
                    <option value="">Select licence type</option>
                    {licenceTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="registrationClass">
                    Registration Class
                  </label>
                  <select
                    id="registrationClass"
                    name="registrationClass"
                    value={formData.registrationClass}
                    onChange={handleInputChange}
                    disabled={!formData.profession}
                  >
                    <option value="">Select registration class</option>
                    {(registrationClasses[formData.profession] || []).map(
                      (registrationClass) => (
                        <option
                          key={registrationClass}
                          value={registrationClass}
                        >
                          {registrationClass}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="issueDate">Issue Date</label>
                  <input
                    id="issueDate"
                    name="issueDate"
                    type="date"
                    value={formData.issueDate}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="expiryDate">Expiry Date</label>
                  <input
                    id="expiryDate"
                    name="expiryDate"
                    type="date"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                  />
                </div>
<div className="form-group full-width">
  <label htmlFor="licenceDocument">
    Licence Copy
  </label>

  {documentLoading ? (
    <p className="credential-document-message">
      Loading licence document...
    </p>
  ) : (
    <>
      {currentDocument && (
        <div className="credential-document-current">
          <div>
            <strong>
              {currentDocument.file_name}
            </strong>

            <span>
              Verification:{" "}
              {currentDocument.verification_status ||
                "Pending"}
            </span>
          </div>

          <div className="credential-document-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={
                handleDownloadDocument
              }
              disabled={
                documentActionLoading
              }
            >
              Download
            </button>

            <button
              type="button"
              className="remove-document-button"
              onClick={
                handleDeleteDocument
              }
              disabled={
                documentActionLoading
              }
            >
              Delete
            </button>
          </div>
        </div>
      )}

      <input
        id="licenceDocument"
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        onChange={handleDocumentChange}
        disabled={
          saving ||
          documentActionLoading
        }
      />

      {selectedDocument && (
        <p className="selected-document-name">
          Selected:{" "}
          <strong>
            {selectedDocument.name}
          </strong>
        </p>
      )}

      <small className="form-help-text">
        Upload a PDF, JPG, JPEG or PNG.
        Maximum file size: 10 MB.
        {currentDocument
          ? " Selecting another file will replace the current document when you save."
          : ""}
      </small>
    </>
  )}
</div>

              </div>

              <label className="primary-checkbox">
                <input
                  type="checkbox"
                  name="isPrimary"
                  checked={formData.isPrimary}
                  onChange={handleInputChange}
                />
                <span>Set as primary licence</span>
              </label>

             <div className="modal-actions">
  <button
    type="button"
    className="cancel-button"
    onClick={handleCloseModal}
    disabled={
      saving ||
      documentActionLoading ||
      documentLoading
    }
  >
    Cancel
  </button>

  <button
    type="submit"
    className="save-button"
    disabled={
      saving ||
      documentActionLoading ||
      documentLoading
    }
  >
    {saving
      ? selectedDocument
        ? "Saving and Uploading..."
        : editingLicence
          ? "Updating..."
          : "Saving..."
      : editingLicence
        ? "Update Licence"
        : "Save Licence"}
  </button>
</div>
</form>
</div>
</div>
)}
</main>
);
};
export default ProfessionalLicenses;
